import { Search } from "@mui/icons-material";
import {
  Box,
  TextField,
  Typography,
  useMediaQuery,
  Button
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tags } from "../../assets/dummyData";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";

const HomePageSearchBar = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("Women");

  const [index, setIndex] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [showText, setShowText] = useState(true);

  const { homepageConfig } = useSelector((state) => state.uiContent);
  const queries = homepageConfig?.searchBarQueries;

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));

  useEffect(() => {
    const currentTag = tags[index]?.name || "a matching co-ord set";
    setTypedText("");
    setShowText(true);

    let charIndex = 0;
    const typingInterval = setInterval(() => {
      setTypedText(currentTag.slice(0, charIndex + 1));
      charIndex++;
      if (charIndex === currentTag.length) {
        clearInterval(typingInterval);
        setTimeout(() => setShowText(false), 1500);
      }
    }, 80);

    return () => clearInterval(typingInterval);
  }, [index]);

  useEffect(() => {
    if (!showText) {
      const timeout = setTimeout(() => {
        setIndex((prev) => (prev + 1) % tags.length);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [showText]);

  const handleSearch = () => {
    if (search.trim().length > 0) {
      navigate('/search', { state: { search, gender } });
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#FFFFFF",
        backgroundImage: "radial-gradient(circle at 50% -20%, rgba(255,107,0,0.08) 0%, rgba(255,255,255,0) 70%)",
        pt: isMobile ? 6 : isTablet ? 8 : 10,
        pb: isMobile ? 4 : isTablet ? 6 : 8,
        px: 2,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        borderBottom: "1px solid #EAEAEA"
      }}
    >


      <Box
        sx={{
          maxWidth: "800px",
          width: "100%",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          <Typography
            sx={{
              fontFamily: "'Lora', serif",
              fontSize: { xs: "32px", sm: "44px", md: "54px", lg: "64px" },
              fontWeight: 500,
              color: "#111111",
              lineHeight: 1.1,
              mb: { xs: 1, md: 1.5 },
              letterSpacing: "-0.03em",
            }}
          >
            Find the exact outfit you <br />
            <Box
              component="span"
              sx={{
                fontStyle: "italic",
                fontWeight: 600,
                background: "linear-gradient(110deg, #FF6B00 0%, #FF6B00 40%, #FFD166 50%, #FF6B00 60%, #FF6B00 100%)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: "neon-shimmer 5s ease-in-out infinite",
                "@keyframes neon-shimmer": {
                  "0%": {
                    backgroundPosition: "150% center",
                  },
                  "50%": {
                    backgroundPosition: "-50% center",
                  },
                  "100%": {
                    backgroundPosition: "-50% center",
                  },
                },
              }}
            >
              have in mind.
            </Box>
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.5, sm: 1.5 },
              mb: 4,
              px: { xs: 2, sm: 2 },
              py: { xs: 1, sm: 0.75 },
              borderRadius: { xs: "12px", sm: "100px" },
              backgroundColor: "rgba(255, 107, 0, 0.04)",
              border: "1px solid rgba(255, 107, 0, 0.1)",
              textAlign: "center"
            }}
          >
            {/* Group 1: Pulsating Indicator + Text (Always together) */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "8px",
                  height: "8px",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backgroundColor: "#FF6B00",
                    borderRadius: "50%",
                    animation: "pulseRing 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                    "@keyframes pulseRing": {
                      "0%": { transform: "scale(0.8)", boxShadow: "0 0 0 0 rgba(255, 107, 0, 0.4)" },
                      "70%": { transform: "scale(1)", boxShadow: "0 0 0 6px rgba(255, 107, 0, 0)" },
                      "100%": { transform: "scale(0.8)", boxShadow: "0 0 0 0 rgba(255, 107, 0, 0)" }
                    }
                  }}
                />
                <Box
                  sx={{
                    position: "relative",
                    width: "6px",
                    height: "6px",
                    backgroundColor: "#FF6B00",
                    borderRadius: "50%",
                  }}
                />
              </Box>

              <Typography sx={{ fontFamily: "Poppins-Medium", fontSize: { xs: "10.5px", md: "14px" }, color: "#555555", letterSpacing: "0.2px" }}>
                Search across 200,000+ products from top brands
              </Typography>
            </Box>

            {/* Group 2: Explore Brands */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                onClick={() => navigate('/brands')}
                sx={{
                  fontFamily: "Poppins-Medium",
                  fontSize: { xs: "10.5px", md: "14px" },
                  color: "#FF6B00",
                  textDecoration: "underline",
                  cursor: "pointer",
                  "&:hover": { color: "#E66000" }
                }}
              >
                View All Brands
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* ─── Gender Selector ─────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              background: "#F5F5F5",
              borderRadius: "100px",
              p: "4px",
              gap: "2px",
              boxShadow: "inset 0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            {["Women", "Men", "Kids"].map((g) => {
              const isActive = gender === g;
              return (
                <Box
                  key={g}
                  onClick={() => setGender(g)}
                  sx={{
                    px: { xs: 2.5, sm: 3.5 },
                    py: "8px",
                    borderRadius: "100px",
                    cursor: "pointer",
                    fontFamily: "Poppins-Medium",
                    fontSize: { xs: "13px", sm: "14px" },
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#fff" : "#666",
                    background: isActive
                      ? "#FF9B80"
                      : "transparent",
                    boxShadow: isActive
                      ? "0 4px 12px rgba(255,107,0,0.2)"
                      : "none",
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      color: isActive ? "#fff" : "#FF6B00",
                      background: isActive
                        ? "#FF9B80"
                        : "rgba(255,107,0,0.06)",
                    },
                  }}
                >
                  {g}
                </Box>
              );
            })}
          </Box>
        </Box>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: "680px",
              display: "flex",
              alignItems: "center",
              background: "#FFFFFF",
              borderRadius: "20px",
              padding: { xs: "4px 6px 4px 16px", sm: "6px 8px 6px 24px" },
              boxShadow: "0 8px 30px rgba(0,0,0,0.04)",
              border: "1.5px solid #EBEBEB",
              transition: "all 0.35s cubic-bezier(0.2, 0.8, 0.2, 1)",
              "&:hover": {
                borderColor: "rgba(255, 107, 0, 0.4)",
                boxShadow: "0 8px 30px rgba(255, 107, 0, 0.04), inset 0 2px 8px rgba(255, 107, 0, 0.02)",
              },
              "&:focus-within": {
                borderColor: "#FF6B00",
                boxShadow: "0 0 0 4px rgba(255, 107, 0, 0.12), 0 8px 35px rgba(255, 107, 0, 0.08), inset 0 2px 10px rgba(255, 107, 0, 0.04)",
              }
            }}
          >
            {/* Search Icon for Desktop Only */}
            <Search sx={{ display: { xs: "none", sm: "block" }, color: "#999", fontSize: "24px", mr: 1.5 }} />

            <Box sx={{ position: "relative", flex: 1, height: isMobile ? "50px" : "60px", display: "flex", alignItems: "center" }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: { xs: "12px", sm: "16px", md: "18px" }, // Reduced by 1 on mobile
                  fontFamily: "Poppins-Regular",
                  color: "#111",
                }}
              />
              {!search && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#A0A0A0",
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    fontSize: { xs: "12px", sm: "16px", md: "18px" }, // Reduced by 1 on mobile
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <span style={{ marginRight: "6px", flexShrink: 0 }}>Try</span>
                  <AnimatePresence mode="wait">
                    {showText && (
                      <motion.span
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          fontFamily: "Poppins-Medium",
                          color: "#777",
                          overflow: "hidden",
                          lineHeight: 1.3
                        }}
                      >
                        "{typedText}"
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Box>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: { xs: 0.5, sm: 1 }, ml: { xs: 1, sm: 2 } }}>
              <Button
                onClick={() => navigate("/image-search")}
                variant="outlined"
                sx={{
                  minWidth: "auto",
                  padding: { xs: "6px", sm: "8px" },
                  borderRadius: "14px",
                  borderColor: "#EAEAEA",
                  color: "#555",
                  "&:hover": {
                    borderColor: "#FF6B00",
                    backgroundColor: "rgba(255,107,0,0.04)",
                    color: "#FF6B00"
                  }
                }}
                title="Search by Image"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </Button>
              <Button
                onClick={handleSearch}
                variant="contained"
                disableElevation
                sx={{
                  borderRadius: "14px",
                  background: "#FF9B80",
                  color: "#FFF",
                  minWidth: "auto",
                  padding: { xs: "6px", sm: "8px 24px" }, // small icon padding on mobile, text padding on desktop
                  "&:hover": {
                    background: "#ff8a6a",
                  }
                }}
              >
                {/* Search Icon for Mobile */}
                <Search sx={{ display: { xs: "block", sm: "none" }, fontSize: "18px" }} />
                {/* Text for Desktop */}
                <Typography sx={{ display: { xs: "none", sm: "block" }, textTransform: "none", fontFamily: "Poppins-Medium", fontSize: "15px" }}>
                  Search
                </Typography>
              </Button>
            </Box>
          </Box>
        </motion.div>

        {/* Tagline before queries */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          style={{ width: "100%", textAlign: "center", marginTop: "24px", marginBottom: "-8px" }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins-Medium",
              fontSize: { xs: "8px", sm: "10px" },
              color: "#535353ff",
              letterSpacing: "1.2px",
              textTransform: "uppercase",
              opacity: 0.8
            }}
          >
            Describe it like a friend. We'll find it like a stylist.
          </Typography>
        </motion.div>

        {/* 3 Clickable Queries beneath search bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          style={{ width: "100%", display: "flex", justifyContent: "center", gap: "8px", marginTop: "24px", flexWrap: "wrap", padding: "0 10px" }}
        >
          {queries && queries[gender]?.map((q, idx) => (
            <motion.div
              key={`${gender}-${idx}`}
              animate={{
                rotate: [0, -2, 2, -2, 1.5, -1, 0.5, 0],
                scale: [1, 1.03, 1, 1.02, 1, 1.01, 1],
              }}
              transition={{
                duration: 1,
                ease: "easeInOut",
                repeat: Infinity,
                repeatDelay: 0.5,
                delay: 1,
              }}
            >
              <Box
                onClick={() => navigate('/search', { state: { search: q, gender } })}
                sx={{
                  background: "rgba(125, 211, 224, 0.08)",
                  border: "1px solid rgba(125, 211, 224, 0.25)",
                  color: "#5abccb",
                  px: { xs: 1.25, sm: 2 },
                  py: { xs: 0.5, sm: 0.5 },
                  borderRadius: "100px",
                  fontSize: { xs: "11px", sm: "13px" },
                  fontFamily: "Poppins-Medium",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: "rgba(125, 211, 224, 0.15)",
                    transform: "translateY(-1px)"
                  },
                  "&:active": {
                    transform: "scale(0.97)"
                  }
                }}
              >
                "{q}"
              </Box>
            </motion.div>
          ))}
        </motion.div>
      </Box>
    </Box>
  );
};

export default HomePageSearchBar;
